<script setup lang="ts">
import type { InputPrimaryEvent } from '@shared/ui/fields'

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { DropZoneArea } from '@shared/ui/other'
import { InputPrimary } from '@shared/ui/fields'
import { BtnIcon } from '@shared/ui/buttons'

import { useImageUploader, ASSESSMENT_IMAGE_ACCEPT } from '../../../model/assessments/image-uploader.hook'

/**
 * One image asset: drop a file in or paste a URL, and see what you chose.
 *
 * Owns the document's `asset.url` and emits the replacement; which kind of value it holds is
 * `media-asset.hook`'s business. The drop area shows only while the field is empty — replacing is
 * Remove then drop again. Two columns: the picture (1/3), then the URL field and `#fields` (2/3).
 */
defineOptions({
  name: 'ImageUploader',
})

const props = withDefaults(defineProps<{
  value?: string
  /** A heading over the uploader. Omitted by default — inside a content block the card already
   *  says what this is. The whiteboard passes one, where the image is a field among others. */
  label?: string
  /** Rendered as the thumbnail's `alt` — pass the block's own alt text where there is one. */
  alt?: string
  required?: boolean
}>(), {
  value: '',
  label: '',
  alt: '',
  required: false,
})

const emit = defineEmits<{
  'update:value': [value: string]
}>()

const {
  refSrc,
  refIsResolving,
  refHasImage,
  refExternalUrl,
  refIsUploading,
  refProgress,
  refError,
  refCanUpload,
  handleFiles,
  handleUrlChange,
  handleRemove,
} = useImageUploader({
  value: () => props.value,
  onChange: value => emit('update:value', value),
})
</script>

<template>
  <div class="image-uploader">
    <!-- `body2`, the weight every other field label carries (`InputPrimary`, `AppTextarea`) — this
         is a field label, not a section subtitle. -->
    <AppText
      v-if="label"
      name="body2"
      color="slate-700"
    >
      {{ required ? `${label} *` : label }}
    </AppText>

    <div class="image-uploader__columns">
      <div class="image-uploader__media">
        <div
          v-if="refHasImage"
          class="image-uploader__preview"
        >
          <img
            v-if="refSrc"
            class="image-uploader__thumb"
            :src="refSrc"
            :alt="alt"
            :title="value"
          >
          <!-- Never an `<img>` with an empty `src`: a broken-image icon reads as a failure when
               the real state is "still signing the link" or "the object is gone". -->
          <div
            v-else
            class="image-uploader__thumb image-uploader__thumb--blank"
          >
            <AppText
              name="body2"
              color="slate-500"
            >
              {{ refIsResolving ? 'Loading preview…' : 'No preview' }}
            </AppText>
          </div>

          <!-- One control, in the corner the other cards put theirs: the picture identifies
               itself, so neither its file name nor "uploaded / external" earns a line under it.
               The full value is still on the thumbnail's own `title`. -->
          <BtnIcon
            :icon="faTrashCan"
            size="small"
            color="red"
            class="image-uploader__remove"
            title="Remove image"
            :disabled="refIsUploading"
            @click="handleRemove"
          />
        </div>

        <!-- Only when the field is empty: with an image chosen, the drop area is the largest
             thing in the block and says "add" next to something already added. Remove is the way
             back to it. -->
        <DropZoneArea
          v-if="!refHasImage"
          title="Drag & Drop an image here"
          browse-text="Browse images"
          :accept="ASSESSMENT_IMAGE_ACCEPT"
          :multiple="false"
          :disabled="refIsUploading || !refCanUpload"
          @files="handleFiles"
        />

        <AppText
          v-if="refIsUploading"
          name="body2"
          color="slate-500"
        >
          Uploading… {{ refProgress }}%
        </AppText>

        <!-- Tied to the drop area it explains, so it does not linger next to an image that is
             already there and needs no upload. -->
        <AppText
          v-if="!refCanUpload && !refHasImage"
          name="body2"
          color="slate-500"
        >
          Save the assessment template before uploading images.
        </AppText>

        <AppText
          v-if="refError"
          name="body2"
          color="red-500"
        >
          {{ refError }}
        </AppText>
      </div>

      <div class="image-uploader__fields">
        <!-- Still here on purpose: an image already hosted elsewhere is a legitimate asset, and
             every document authored before uploads existed holds one. Empty for an uploaded
             object, whose path is not something to hand-edit. -->
        <InputPrimary
          name="imageUrl"
          label="…or paste an image URL"
          optional-label="optional"
          :value="refExternalUrl"
          :disabled="refIsUploading"
          placeholder="https://…"
          @change="(event: InputPrimaryEvent) => handleUrlChange(String(event.value))"
        />

        <!-- Whatever else describes this image. The image block puts its alt text here. -->
        <slot name="fields" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./ImageUploader.scss" />
